/**
 * toDisplayString — safe coercion of unknown values to a renderable string.
 *
 * Origin (PR-11): Legacy `quote_templates` rows (pre-Fix-A) may store `name`
 * or `description` as an OBJECT due to a client-side arg-shape bug in
 * `QuoteBuilderPage`'s Save-as-Template handler (positional args vs.
 * object-arg mismatch). Rendering such a value directly triggers React
 * error #31 ("objects are not valid as a React child") and white-screens
 * the page. This helper renders SOMETHING sane instead of crashing.
 *
 * Extracted to shared utils (PR-11 FIX, 2026-07-22) so every consumer that
 * renders template / line-item text (Settings page, ChooseTemplateModal,
 * builder toasts, etc.) uses the same coercion. The eventual full fix is a
 * backend validator + DB cleanup; until then, defensive coercion keeps every
 * surface alive.
 *
 * Safe for:
 *   - strings           → returned as-is
 *   - numbers, booleans → String(v)
 *   - null, undefined   → fallback (default '')
 *   - objects with `.name` (string)        → v.name
 *   - objects with `.description` (string) → v.description
 *   - anything else     → fallback or '(malformed value)'
 *
 * @param {unknown} v        Value to coerce.
 * @param {string} [fallback=''] What to return for null/undefined/malformed.
 * @returns {string}
 */
export function toDisplayString(v, fallback = '') {
  if (v == null) return fallback;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') {
    // Try common nested shapes before giving up.
    if (typeof v.name === 'string') return v.name;
    if (typeof v.description === 'string') return v.description;
    return fallback || '(malformed value)';
  }
  return fallback;
}
